import { Nullable } from 'legacy/lut/classes/models/utils'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  FormNumberInput,
  resetModifiedDataByField,
  updateFormDataField,
  updateInvalidData,
} from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'

interface IProps {
  isRequired: boolean
  isFromGlobalPartner?: boolean
  currency?: string
  valueType?: string
  initialValue: Nullable<number>
  className?: string
  shouldAddToBridges?: boolean
  formKey: string
  goal?: number
  incrementalFieldName: string
  goalFieldName?: string
  usePlaceholder?: boolean
  autoEnableAddToBridges?: boolean
}

export const Incremental = ({
  isRequired,
  initialValue,
  currency = '',
  valueType = '',
  className,
  shouldAddToBridges = false,
  formKey,
  incrementalFieldName,
  isFromGlobalPartner = false,
  goal,
  goalFieldName,
  usePlaceholder,
  autoEnableAddToBridges = true,
}: IProps) => {
  const [previousIncrementalField, setPreviousIncrementalField] = useState('')
  const dispatch = useAppDispatch()

  const label = useMemo(() => {
    return `Incremental, ${currency} ${valueType}`
  }, [currency, valueType])

  const handleValidate = useCallback(
    async (value: Nullable<number>) => {
      if (!isRequired) {
        return ''
      }

      if (value === null) {
        return 'This value is required'
      }

      return ''
    },
    [isRequired]
  )

  const resetField = useCallback(() => {
    dispatch(
      resetModifiedDataByField({
        formKey: formKey,
        field: previousIncrementalField,
      })
    )
    dispatch(
      updateInvalidData({
        key: formKey,
        field: previousIncrementalField,
        validationMessage: '',
      })
    )
  }, [dispatch, previousIncrementalField, formKey])

  const handleChange = useCallback(
    (value: Nullable<number>) => {
      if (autoEnableAddToBridges && value && !shouldAddToBridges) {
        dispatch(
          updateFormDataField({
            field: 'shouldAddToBridges',
            value: true,
            formKey: formKey,
          })
        )
      }

      if (isFromGlobalPartner && goalFieldName) {
        dispatch(
          updateFormDataField({
            field: goalFieldName,
            value: value,
            formKey: formKey,
          })
        )
      }
    },
    [
      autoEnableAddToBridges,
      shouldAddToBridges,
      dispatch,
      formKey,
      goalFieldName,
      isFromGlobalPartner,
    ]
  )

  useEffect(() => {
    if (previousIncrementalField !== incrementalFieldName) {
      resetField()
      setPreviousIncrementalField(incrementalFieldName)
    }
  }, [resetField, previousIncrementalField, incrementalFieldName])

  useEffect(() => {
    if (!isRequired) {
      dispatch(
        updateInvalidData({
          key: formKey,
          field: previousIncrementalField,
          validationMessage: '',
        })
      )
    }
  }, [isRequired, dispatch, previousIncrementalField, formKey])

  return (
    <FormNumberInput
      isDisabled={isFromGlobalPartner ? false : !goal}
      isRequired={isRequired}
      formKey={formKey}
      field={incrementalFieldName}
      defaultValue={initialValue}
      label={usePlaceholder ? '' : label}
      placeholder={usePlaceholder ? label : ''}
      className={className}
      onValidate={handleValidate}
      validationType={ValidationType.ONCHANGE}
      onChange={handleChange}
    />
  )
}
